import { T, useGT } from 'gt-react';
import { useTaskTypes } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { exceptionTaskTypeOptions } from './exceptionTaskTypeOptions';

const HAND_OFF = '__hand_off__';

/**
 * Task types anchored to AP invoices — a rule that names one opens a task of
 * that type per exception and closes it when the exception resolves. Types
 * already used by another rule are hidden (one open task per type and
 * invoice). Empty value = the exception rides the wired hand-off task.
 */
export function ExceptionTaskTypePicker({
  id,
  value,
  takenKeys,
  disabled,
  onChange,
}: {
  id?: string;
  value: string;
  /** Keys picked by other rules in the set. */
  takenKeys: ReadonlySet<string>;
  disabled?: boolean;
  onChange: (taskTypeKey: string) => void;
}) {
  const gt = useGT();
  const { data, isLoading, error } = useTaskTypes();
  const handOffLabel = gt('Hand-off task (no task type)', {
    context:
      'exception rule task type picker option: route on the task wired into the match node',
  });
  const { options: typeOptions, missingValue } = exceptionTaskTypeOptions(
    data ?? [],
    value,
    takenKeys,
  );
  const options: SelectDropdownOption[] = [
    { id: HAND_OFF, label: handOffLabel, display: handOffLabel },
    ...typeOptions.map((type) => ({
      id: type.key,
      label: `${type.name} (${type.key})`,
      display: type.name,
    })),
  ];
  // A saved key the list no longer offers (archived, re-anchored) stays
  // visible so the user can see why a save fails and pick another type.
  if (missingValue && !isLoading) {
    const missing = gt('{key} (unavailable)', {
      key: value,
      context:
        'exception rule task type picker: a saved task type that is archived or no longer anchored to invoices',
    });
    options.push({ id: value, label: missing, display: missing });
  }
  const selected =
    options.find((option) => option.id === (value || HAND_OFF)) ?? null;

  return (
    <div className="flex flex-col gap-1">
      <SelectDropdown
        id={id}
        options={options}
        value={selected}
        onChange={(option) =>
          onChange(!option || option.id === HAND_OFF ? '' : option.id)
        }
        placeholder={handOffLabel}
        searchPlaceholder={gt('Search task types…', {
          context: 'search box in the exception rule task type picker',
        })}
        size="sm"
        fullWidth
        disabled={disabled || isLoading}
      />
      {error ? (
        <p className="text-xs text-destructive">
          <T context="error loading task types for the exception rule task type picker">
            Could not load task types.
          </T>
        </p>
      ) : null}
    </div>
  );
}
